import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    BadgeCheck, Bell, CalendarDays, Check, CheckCheck, ChevronLeft, ChevronRight, Clock3,
    CreditCard, Hash, Mail, MapPin, Phone, Power, PowerOff, Search,
    Eye, Gift, Plus, RotateCw, ShieldAlert, ShieldCheck, Store, Trash2, UserCheck, UserRound, UsersRound, X,
} from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import Navbar from '../../components/Navbar';
import ConfirmDialog from '../../components/ConfirmDialog';
import RejectVerificationModal from '../../components/RejectVerificationModal';
import API from '../../utils/api';
import { onAdminRefresh } from '../../utils/adminSocket';
import RowActions from './RowActions';
import AdminRegistrationModal from './AdminRegistrationModal';
import toast from 'react-hot-toast';
import './Users.css';

const PAGE_SIZE = 10;

const ROLE_META = {
    member: { label: 'Member', Icon: UsersRound, className: 'member' },
    service: { label: 'Provider', Icon: Store, className: 'provider' },
    admin: { label: 'Admin', Icon: ShieldCheck, className: 'admin' },
};

const tableColumnsFor = isProviders => [
    { label: '#', Icon: Hash, className: 'users-col-index' },
    { label: isProviders ? 'Business' : 'Name', Icon: isProviders ? Store : UserRound },
    { label: 'Mobile', Icon: Phone },
    { label: 'Email', Icon: Mail, className: 'users-hide-md' },
    { label: isProviders ? 'Area / City' : 'Gender', Icon: isProviders ? MapPin : UserRound },
    { label: 'Plan', Icon: CreditCard },
    { label: 'Account status', Icon: BadgeCheck },
    { label: 'Verifier', Icon: UserCheck, className: 'users-hide-xl' },
    { label: 'Joined', Icon: CalendarDays, className: 'users-hide-lg' },
    { label: '', Icon: null, className: 'users-col-actions' },
];

const displayNameFor = account => account?.role === 'service'
    ? account?.businessName || account?.ownerName || account?.name || 'Unnamed business'
    : account?.name || account?.profileName || account?.businessName || account?.ownerName || 'Incomplete member account';

// scope 'member': the Users page lists members only. scope 'service': the Service Providers page lists providers only.
const Users = ({ scope = 'member' }) => {
    const isProviders = scope === 'service';
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [providers, setProviders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState('all');
    const [page, setPage] = useState(1);
    const [pendingDelete, setPendingDelete] = useState(null);
    const [rejectingMember, setRejectingMember] = useState(null);
    const [registrationOpen, setRegistrationOpen] = useState(false);
    const closeRegistration = useCallback(() => setRegistrationOpen(false), []);

    // silent = true: refresh the table in the background (no spinner) when the server says something changed
    const fetchAccounts = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        // the Members page and the Service Providers page share this component, so drop the other page's list
        if (isProviders) setUsers([]); else setProviders([]);
        try {
            // Administrators are managed on the Admin Access page, so only members and providers are loaded here.
            if (isProviders) {
                const providersResponse = await API.get('/admin/vendors');
                setProviders(providersResponse.data.vendors || []);
            } else {
                const usersResponse = await API.get('/admin/users');
                setUsers(usersResponse.data.users || []);
            }
        } catch (error) {
            toast.error('Failed to load accounts');
        } finally {
            setLoading(false);
        }
    }, [isProviders]);

    useEffect(() => {
        fetchAccounts();
        // realtime: when someone registers, the server pings the admin panel and the new row appears here at once
        return onAdminRefresh(() => fetchAccounts(true));
    }, [fetchAccounts]);

    // A new account stays "unread" (a dot and a NEW tag) until an admin opens it or marks it read.
    const markAccountRead = useCallback(async (account) => {
        if (!account?.unreadByAdmin) return;
        const update = current => current.map(item => (item._id === account._id ? { ...item, unreadByAdmin: false } : item));
        if (account.role === 'service') setProviders(update); else setUsers(update);
        try {
            await API.put(`/admin/users/${account._id}/mark-read`);
            window.dispatchEvent(new Event('usersSeen')); // refresh the sidebar badge
        } catch (error) { /* the next refresh shows the real state */ }
    }, []);

    const markAllRead = async () => {
        try {
            await API.put('/admin/mark-users-read', { scope });
            const clear = current => current.map(item => ({ ...item, unreadByAdmin: false }));
            if (isProviders) setProviders(clear); else setUsers(clear);
            window.dispatchEvent(new Event('usersSeen'));
            toast.success('All marked as read');
        } catch (error) {
            toast.error(error.response?.data?.message || 'Could not mark them as read');
        }
    };

    useEffect(() => setPage(1), [search, filter]);

    const accounts = useMemo(() => [
        // Older databases used `user` before the role was renamed to
        // `member`. Treat every non-provider record on this page as a member
        // so its action menu is never empty.
        ...users.map(user => ({ ...user, role: user.role === 'service' ? 'service' : 'member' })),
        ...providers.map(user => ({ ...user, role: 'service' })),
    ], [users, providers]);

    // Member and provider remain separate authentication records. In the
    // admin list, records sharing a mobile number are presented as one person
    // with multiple role badges and role-specific controls.
    const identities = useMemo(() => {
        const grouped = new Map();
        accounts.forEach(account => {
            const sharedKey = account.mobile || account._id;
            const key = account.role === 'admin' ? `admin:${account._id}` : `person:${sharedKey}`;
            const identity = grouped.get(key) || { key, mobile: account.mobile, accounts: [] };
            identity.accounts.push(account);
            identity[account.role] = account;
            grouped.set(key, identity);
        });

        return [...grouped.values()].sort((a, b) => {
            const newestA = Math.max(...a.accounts.map(account => new Date(account.createdAt || 0).getTime()));
            const newestB = Math.max(...b.accounts.map(account => new Date(account.createdAt || 0).getTime()));
            return newestB - newestA;
        });
    }, [accounts]);

    const filtered = useMemo(() => {
        const query = search.trim().toLowerCase();
        return identities.filter(identity => {
            const matchesRole = filter === 'all' || Boolean(identity[filter]);
            const matchesSearch = !query || identity.accounts.some(account => [
                account.memberId, account.providerId, displayNameFor(account), account.businessName, account.ownerName,
                account.mobile, account.email, account.city, account.district, account.state, account.country, account.category,
            ].some(value => String(value || '').toLowerCase().includes(query)));
            return matchesRole && matchesSearch;
        });
    }, [identities, filter, search]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const currentPage = Math.min(page, totalPages);
    const pageStart = (currentPage - 1) * PAGE_SIZE;
    const paginatedUsers = filtered.slice(pageStart, pageStart + PAGE_SIZE);
    const tableColumns = tableColumnsFor(isProviders);

    const handleVerify = async account => {
        try {
            const endpoint = account.isVerified ? 'unverify' : 'verify';
            const response = await API.put(`/admin/users/${account._id}/${endpoint}`);
            const update = current => current.map(item => item._id === account._id
                ? { ...item, isVerified: response.data.user.isVerified }
                : item);
            if (account.role === 'service') setProviders(update);
            else setUsers(update);
            toast.success(response.data.message);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to update verification');
        }
    };

    const handleToggleAccountActive = async account => {
        try {
            const response = await API.put(`/admin/users/${account._id}/toggle-active`);
            const update = current => current.map(item => item._id === account._id ? response.data.user : item);
            if (account.role === 'service') setProviders(update);
            else setUsers(update);
            toast.success(response.data.message);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to update account access');
        }
    };

    // Opens the confirmation modal; the actual delete only runs once the
    // user confirms in ConfirmDialog (see confirmDeleteAccount below).
    const handleDelete = account => setPendingDelete(account);

    const confirmDeleteAccount = async () => {
        const account = pendingDelete;
        if (!account) return;
        const name = displayNameFor(account);
        setPendingDelete(null);
        try {
            const endpoint = account.role === 'admin'
                ? `/admin/admins/${account._id}`
                : `/admin/users/${account._id}`;
            await API.delete(endpoint);
            toast.success(`${name}'s ${account.role} account deleted`);
            await fetchAccounts();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to delete account');
        }
    };

    // everything the row's buttons used to do, in one menu; "View full detail" opens the member on the Profiles page
    const menuItemsFor = (identity) => {
        const items = [];
        const { member, service: provider, admin } = identity;
        if (member) {
            if (member.hasProfile) {
                items.push({ label: 'View full detail', Icon: Eye, tone: 'primary', onClick: () => { markAccountRead(member); navigate(`/profiles?q=${encodeURIComponent(member.mobile || member.name || '')}&open=1`); } });
                items.push({ label: member.isVerified ? 'Unverify member' : 'Verify member', Icon: member.isVerified ? X : Check, tone: member.isVerified ? 'danger' : 'success', onClick: () => handleVerify(member) });
            } else {
                items.push({ label: 'Profile not completed', Icon: ShieldAlert, tone: 'warn', onClick: () => toast('This member has not completed a profile yet.') });
            }
            if (member.hasProfile && !member.isVerified) {
                items.push({
                    label: member.verificationStatus === 'rejected' ? 'Update rejection message' : 'Reject verification',
                    Icon: ShieldAlert,
                    tone: 'danger',
                    onClick: () => setRejectingMember(member)
                });
            }
            items.push({ label: member.isActive !== false ? 'Deactivate account' : 'Activate account', Icon: member.isActive !== false ? PowerOff : Power, tone: member.isActive !== false ? 'warn' : 'success', onClick: () => handleToggleAccountActive(member) });
            items.push(member.isPremium
                ? { label: 'Move to free plan', Icon: CreditCard, onClick: () => navigate(`/plans?user=${member._id}&view=1`) }
                : { label: 'Upgrade to premium', Icon: CreditCard, onClick: () => navigate(`/plans?user=${member._id}`) });
            items.push({ label: 'Delete member account', Icon: Trash2, tone: 'danger', onClick: () => handleDelete(member) });
        }
        if (provider) {
            if (member) items.push({ section: 'Provider account' });
            items.push({ label: 'View full detail & services', Icon: Eye, tone: 'primary', onClick: () => { markAccountRead(provider); navigate(`/service-providers/${provider._id}`); } });
            items.push(provider.isPremium
                ? { label: 'Move to free plan', Icon: CreditCard, onClick: () => navigate(`/plans?user=${provider._id}&view=1`) }
                : { label: 'Upgrade to premium', Icon: CreditCard, onClick: () => navigate(`/plans?user=${provider._id}`) });
            items.push({ label: provider.isVerified ? 'Unverify provider' : 'Verify provider', Icon: provider.isVerified ? X : Check, tone: provider.isVerified ? 'danger' : 'success', onClick: () => handleVerify(provider) });
            items.push({ label: provider.isActive !== false ? 'Deactivate account' : 'Activate account', Icon: provider.isActive !== false ? PowerOff : Power, tone: provider.isActive !== false ? 'warn' : 'success', onClick: () => handleToggleAccountActive(provider) });
            items.push({ label: 'Delete provider account', Icon: Trash2, tone: 'danger', onClick: () => handleDelete(provider) });
        }
        if (admin) items.push({ label: 'Delete admin account', Icon: Trash2, tone: 'danger', onClick: () => handleDelete(admin) });
        return items;
    };

    const unreadTotal = (isProviders ? providers : users).filter(account => account.unreadByAdmin).length;

    const stats = [
        isProviders
            ? { label: 'Service providers', value: providers.length, Icon: Store, tone: 'amber' }
            : { label: 'Members', value: users.length, Icon: UserRound, tone: 'green' },
        { label: 'New / unread', value: unreadTotal, Icon: Bell, tone: 'violet' },
    ];

    const filters = [
        { id: 'all', label: 'All', count: identities.length },
        { id: 'member', label: 'Members', count: users.length },
        { id: 'service', label: 'Providers', count: providers.length },
    ];

    return (
        <div className="users-layout">
            <Sidebar />
            <main className="admin-content users-content">
                <Navbar title={<span className="users-title">{isProviders ? <Store size={19} /> : <UsersRound size={19} />} {isProviders ? 'Service Providers' : 'Users'}</span>} />
                <div className="users-page">
                    <div className="users-heading">
                        <div>
                            <h2>{isProviders ? 'Service providers' : 'Members'}</h2>
                            <p>{isProviders ? 'Wedding service providers and their business accounts.' : 'Matrimony members only. Service providers are on the Service Providers page; administrators on Admin Access.'}</p>
                        </div>
                        <div className="users-heading-actions">
                            {unreadTotal > 0 && (
                                <button type="button" className="users-mark-all" onClick={markAllRead}>
                                    <CheckCheck size={16} /> Mark all as read ({unreadTotal})
                                </button>
                            )}
                            <button type="button" className="users-add-account" onClick={() => setRegistrationOpen(true)}>
                                <Plus size={16} /> Add {isProviders ? 'Service Provider' : 'Member'}
                            </button>
                            <span className="users-total">{isProviders ? <Store size={15} /> : <UsersRound size={15} />} {identities.length} {isProviders ? 'providers' : 'members'}</span>
                        </div>
                    </div>

                    <section className="users-stats" aria-label="Account overview">
                        {stats.map(({ label, value, Icon, tone }) => (
                            <article className="users-stat-card" key={label}>
                                <span className={`users-stat-icon ${tone}`}><Icon size={19} /></span>
                                <div><strong>{value}</strong><span>{label}</span></div>
                            </article>
                        ))}
                    </section>

                    <section className="users-panel">
                        <div className="users-toolbar">
                            <label className="users-search">
                                <Search size={17} />
                                <input type="search" placeholder="Search name, mobile, email, city..." value={search} onChange={event => setSearch(event.target.value)} />
                            </label>
                            <div className="users-filters" aria-label="Filter people by role" style={{ display: "none" }}>
                                {filters.map(option => (
                                    <button key={option.id} type="button" className={filter === option.id ? 'active' : ''} onClick={() => setFilter(option.id)}>
                                        {option.label}<span>{option.count}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {loading ? (
                            <div className="users-state"><span className="users-spinner" />Loading accounts...</div>
                        ) : (
                            <>
                                <div className="users-table-scroll">
                                    <table className="users-table">
                                        <thead>
                                            <tr>
                                                {tableColumns.map(({ label, Icon, className }) => (
                                                    <th key={label} className={className || ''}><span>{Icon && <Icon size={14} />}{label}</span></th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {paginatedUsers.map((identity, index) => {
                                                const member = identity.member;
                                                const provider = identity.service;
                                                const admin = identity.admin;
                                                const primary = member || provider || admin;
                                                const displayName = displayNameFor(primary);
                                                const joinedAt = identity.accounts.reduce((earliest, account) => {
                                                    if (!account.createdAt) return earliest;
                                                    return !earliest || new Date(account.createdAt) < new Date(earliest) ? account.createdAt : earliest;
                                                }, null);

                                                const unread = identity.accounts.some(account => account.unreadByAdmin);

                                                return (
                                                    <tr key={identity.key} className={unread ? 'users-row-unread' : ''} onClick={() => identity.accounts.forEach(markAccountRead)}>
                                                        <td className="users-row-number">{unread && <span className="users-new-dot" title="New: not opened yet" aria-label="Unread" />}{pageStart + index + 1}</td>
                                                        <td>
                                                            <div className="users-person">
                                                                <span className="users-avatar">{displayName.charAt(0).toUpperCase()}</span>
                                                                <div>
                                                                    <strong>{displayName}{unread && <span className="users-new-pill">NEW</span>}</strong>
                                                                    {member && <small><UserRound size={11} />Member ID · {member.memberId || 'Pending assignment'}</small>}
                                                                    {provider && <small><Store size={11} />Provider ID · {provider.providerId || 'Pending assignment'}</small>}
                                                                    {member && provider && <small className="users-linked-role"><Store size={11} />{provider.businessName} · linked provider</small>}
                                                                    {!member && provider?.category && <small><Store size={11} />{provider.category}</small>}
                                                                    {member?.profileFor && member.profileFor !== 'Myself' && <small><UsersRound size={11} />For {member.profileFor}: {member.profileName || '-'}</small>}
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td><span className="users-cell-with-icon"><Phone size={14} />{identity.mobile || '-'}</span></td>
                                                        <td className="users-hide-md">
                                                            <div className="users-multi-value">
                                                                {identity.accounts.map(account => <span key={account._id}><Mail size={13} />{account.email || '-'}</span>)}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="users-multi-value">
                                                                {isProviders && provider && <span><MapPin size={13} />{[provider.city, provider.district, provider.state].filter(Boolean).join(', ') || '-'}</span>}
                                                                {!isProviders && member && <span><UserRound size={13} />{member.gender || '-'}</span>}
                                                                {admin && <span><ShieldCheck size={13} />Administrator</span>}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            {(member || provider) ? (
                                                                <span className={`users-badge plan ${(member || provider).isPremium ? 'premium' : 'free'}`}>
                                                                    <CreditCard size={13} />{(member || provider).isPremium ? 'Premium' : 'Free'}
                                                                </span>
                                                            ) : <span className="users-muted">-</span>}
                                                            {(member || provider)?.isPremium && (member || provider).premiumSource === 'gifted' && (
                                                                <span className="users-badge gifted"><Gift size={12} />Gifted by admin</span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <div className="users-status-stack">
                                                                {member && (
                                                                    <span className={`users-badge status ${member.isVerified ? 'verified' : member.verificationStatus === 'rejected' ? 'rejected' : member.verificationStatus === 'resubmitted' ? 'resubmitted' : 'pending'}`}>
                                                                        {member.isVerified ? <Check size={13} /> : member.verificationStatus === 'rejected' ? <ShieldAlert size={13} /> : member.verificationStatus === 'resubmitted' ? <RotateCw size={13} /> : <Clock3 size={13} />}
                                                                        Member {member.isVerified ? 'verified' : member.verificationStatus === 'rejected' ? 'rejected' : member.verificationStatus === 'resubmitted' ? 'resubmitted' : 'pending'}
                                                                    </span>
                                                                )}
                                                                {member && !member.hasProfile && (
                                                                    <span className="users-badge status incomplete"><ShieldAlert size={13} />Profile incomplete</span>
                                                                )}
                                                                {member && (
                                                                    <span className={`users-badge status ${member.isActive !== false ? 'active' : 'inactive'}`}>
                                                                        {member.isActive !== false ? <Power size={13} /> : <PowerOff size={13} />}
                                                                        Member {member.isActive !== false ? 'active' : 'inactive'}
                                                                    </span>
                                                                )}
                                                                {provider && (
                                                                    <span className={`users-badge status ${provider.isVerified ? 'verified' : 'pending'}`}>
                                                                        {provider.isVerified ? <Check size={13} /> : <Clock3 size={13} />}
                                                                        Provider {provider.isVerified ? 'verified' : 'pending'}
                                                                    </span>
                                                                )}
                                                                {provider && (
                                                                    <span className={`users-badge status ${provider.isActive !== false ? 'active' : 'inactive'}`}>
                                                                        {provider.isActive !== false ? <Power size={13} /> : <PowerOff size={13} />}
                                                                        Provider {provider.isActive !== false ? 'active' : 'inactive'}
                                                                    </span>
                                                                )}
                                                                {admin && <span className="users-badge status active"><Check size={13} />Admin active</span>}
                                                            </div>
                                                        </td>
                                                        <td className="users-hide-xl">
                                                            <div className="users-status-stack">
                                                                {member && (
                                                                    <span className={`users-badge status ${member.assignedVerifier ? 'verified' : 'pending'}`}>
                                                                        <UserCheck size={13} />
                                                                        Member: {member.assignedVerifier?.name || 'Unassigned'}
                                                                    </span>
                                                                )}
                                                                {provider && (
                                                                    <span className={`users-badge status ${provider.assignedVerifier ? 'verified' : 'pending'}`}>
                                                                        <UserCheck size={13} />
                                                                        Provider: {provider.assignedVerifier?.name || 'Unassigned'}
                                                                    </span>
                                                                )}
                                                                {!member && !provider && <span className="users-muted">-</span>}
                                                            </div>
                                                        </td>
                                                        <td className="users-hide-lg"><span className="users-cell-with-icon"><CalendarDays size={14} />{joinedAt ? new Date(joinedAt).toLocaleDateString('en-IN') : '-'}</span></td>
                                                        <td className="users-col-actions"><RowActions items={menuItemsFor(identity)} label={`Actions for ${displayName}`} /></td>
                                                    </tr>
                                                );
                                            })}
                                            {paginatedUsers.length === 0 && (
                                                <tr><td colSpan={10}><div className="users-empty"><Search size={24} /><strong>No people found</strong><span>Try a different search or filter.</span></div></td></tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                <footer className="users-pagination">
                                    <span>{filtered.length === 0 ? '0 results' : `Showing ${pageStart + 1}-${Math.min(pageStart + PAGE_SIZE, filtered.length)} of ${filtered.length} people`}</span>
                                    <div>
                                        <button type="button" onClick={() => setPage(value => Math.max(1, value - 1))} disabled={currentPage === 1} aria-label="Previous page"><ChevronLeft size={16} /></button>
                                        <span>Page <strong>{currentPage}</strong> of {totalPages}</span>
                                        <button type="button" onClick={() => setPage(value => Math.min(totalPages, value + 1))} disabled={currentPage === totalPages} aria-label="Next page"><ChevronRight size={16} /></button>
                                    </div>
                                </footer>
                            </>
                        )}
                    </section>
                </div>
            </main>

            <ConfirmDialog
                open={Boolean(pendingDelete)}
                title="Delete account?"
                message={pendingDelete
                    ? `You're about to permanently delete ${displayNameFor(pendingDelete)}'s ${ROLE_META[pendingDelete.role]?.label || 'member'} account.`
                    : ''}
                items={['The account, profile, photos, documents, bookings, interests, chats and listings are all erased', 'Payment invoices are kept for tax records', "The same mobile number's other account (member or provider) is not touched"]}
                confirmLabel="Delete Account"
                danger
                onConfirm={confirmDeleteAccount}
                onCancel={() => setPendingDelete(null)}
            />
            <AdminRegistrationModal
                open={registrationOpen}
                role={isProviders ? 'service' : 'member'}
                onClose={closeRegistration}
                onCreated={fetchAccounts}
            />
            <RejectVerificationModal
                open={Boolean(rejectingMember)}
                target={rejectingMember}
                onClose={() => setRejectingMember(null)}
                onSuccess={(data) => {
                    if (data?.user) {
                        setUsers(prev => prev.map(u => u._id === data.user._id ? { ...u, isVerified: false, verificationStatus: 'rejected', verificationRejectReason: data.user.verificationRejectReason } : u));
                    }
                    fetchAccounts();
                }}
            />
        </div>
    );
};

export default Users;
